import React from 'react';
import ToolTip from '@atlaskit/tooltip';
import defaultTo from 'lodash/defaultTo';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { OpenSidebarButton } from '@css-xp/base';
import { SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import { NoData } from '@css-xp/ppc-common';

interface IncludedProductsCellProps {
  transactionAccountId: string;
  product: generatedTypesNew.TransactionAccountProduct;
  version?: number;
  isAppificationEnabled?: boolean;
}

const getIncludedProductsCellText = ({
  linkedAppsId,
  linkedAddOnsId,
  linkedCollectionProductsId,
  linkedEnterpriseInstancesId,
  linkedSandboxId,
}: {
  [key: string]: string[];
}) => {
  const displayText = [];

  if (linkedCollectionProductsId.length > 0) {
    displayText.push(`${linkedCollectionProductsId.length} product${linkedCollectionProductsId.length > 1 ? 's' : ''}`);
  }
  if (linkedAppsId.length > 0) {
    displayText.push(`${linkedAppsId.length} app${linkedAppsId.length > 1 ? 's' : ''}`);
  }
  if (linkedAddOnsId.length > 0) {
    displayText.push(`${linkedAddOnsId.length} add-on${linkedAddOnsId.length > 1 ? 's' : ''}`);
  }
  if (linkedEnterpriseInstancesId.length > 0) {
    displayText.push(
      `${linkedEnterpriseInstancesId.length} enterprise instance${linkedEnterpriseInstancesId.length > 1 ? 's' : ''}`
    );
  }
  if (linkedSandboxId.length > 0) {
    displayText.push(`${linkedSandboxId.length} sandbox`);
  }

  if (displayText.length > 0) {
    return displayText.join(', ');
  } else {
    return '';
  }
};

const getIdsByRelatesToEntitlement = (product: generatedTypesNew.TransactionAccountProduct) => {
  const idsByRelatesToEntitlement: { [key: string]: string[] } = {};
  product?.relatesToEntitlements?.forEach((relatesToEntitlement) => {
    switch (relatesToEntitlement?.relationshipType as string) {
      case SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE:
        idsByRelatesToEntitlement[SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE] = defaultTo(
          idsByRelatesToEntitlement[SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE],
          []
        ).concat(defaultTo(relatesToEntitlement?.entitlementId, []));
        break;
      case generatedTypesNew.RelationshipPathType.SANDBOX_DEPENDENCE:
        idsByRelatesToEntitlement[generatedTypesNew.RelationshipPathType.SANDBOX_DEPENDENCE] = defaultTo(
          idsByRelatesToEntitlement[generatedTypesNew.RelationshipPathType.SANDBOX_DEPENDENCE],
          []
        ).concat(defaultTo(relatesToEntitlement?.entitlementId, []));
        break;
      case 'ENTERPRISE':
        idsByRelatesToEntitlement['ENTERPRISE'] = defaultTo(idsByRelatesToEntitlement['ENTERPRISE'], []).concat(
          defaultTo(relatesToEntitlement?.entitlementId, [])
        );
        break;
    }
  });
  return idsByRelatesToEntitlement;
};

export const IncludedProductsCell = ({
  transactionAccountId,
  product,
  version,
  isAppificationEnabled,
}: IncludedProductsCellProps) => {
  const idsByRelatesToEntitlement = getIdsByRelatesToEntitlement(product);

  const linkedAppsId = defaultTo(product?.linkedAppsId, []) as string[];
  const linkedAddOnsId = defaultTo(product?.linkedAddOnsId, []) as string[];
  const linkedCollectionProductsId = defaultTo(idsByRelatesToEntitlement?.COLLECTION, []);
  const linkedEnterpriseInstancesId = defaultTo(idsByRelatesToEntitlement?.ENTERPRISE, []);
  const linkedSandboxId = defaultTo(idsByRelatesToEntitlement?.SANDBOX_DEPENDENCE, []);

  const displayText = getIncludedProductsCellText({
    linkedAppsId: linkedAppsId,
    linkedAddOnsId: linkedAddOnsId,
    linkedCollectionProductsId: linkedCollectionProductsId,
    linkedEnterpriseInstancesId: linkedEnterpriseInstancesId,
    linkedSandboxId: linkedSandboxId,
  });

  const totalSubscriptionCount =
    linkedAppsId.length +
    linkedAddOnsId.length +
    linkedCollectionProductsId.length +
    linkedEnterpriseInstancesId.length +
    linkedSandboxId.length;

  return (
    <td>
      {displayText ? (
        <ToolTip content={displayText} hideTooltipOnClick>
          <OpenSidebarButton
            sidebar={SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_PAGE_SIDEBAR.INCLUDED_PRODUCTS}
            sidebarOptions={
              version === 2
                ? undefined
                : {
                    sidebarWidth: '20%',
                    isOverlay: true,
                  }
            }
            payload={{
              transactionAccountId,
              selectedEntitlementDetails: {
                id: product?.id,
                productName: product?.productName,
                entitlementNumber: product?.entitlementNumber,
              },
              linkedAppsId: linkedAppsId,
              linkedAddOnsId: linkedAddOnsId,
              linkedCollectionProductsId: linkedCollectionProductsId,
              linkedEnterpriseInstancesId: linkedEnterpriseInstancesId,
              linkedSandboxId: linkedSandboxId,
              version,
              isAppificationEnabled,
            }}
            testId="included-products-sidebar-button"
            label="Included products"
          >
            {`${totalSubscriptionCount} subscription${totalSubscriptionCount > 1 ? 's' : ''}`}
          </OpenSidebarButton>
        </ToolTip>
      ) : (
        <NoData />
      )}
    </td>
  );
};
